import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { Layout as AntLayout, Button, Drawer, Menu } from 'antd'

import { Feature } from 'core'
import { User } from 'core/application/account/loginUser'
import { Permissions } from 'core/application/account/permissions'
import { UserRoles } from 'core/application/account/userRoles'
import { AdminTask } from 'core/application/adminDashboard/adminTasks'
import { ProductIds } from 'core/application/commons/config'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import Icon from 'common/components/general/Icon'
import CompanyPersonalComponent, { CompanyPersonalSection } from 'common/components/layout/CompanyPersonalComponent'
import AdminDashboardHeader from 'common/components/layout/DashboardHeader'
import Footer from 'common/components/layout/Footer'

import useSharedState from 'common/hooks/useSharedState'

import { Tag } from 'common/components'
import { useFeatureFlag } from 'common/hooks'

import useCurrentProduct from '../common/hooks/useCurrentProduct'
import { ROUTE_PATHS } from '../common/routePaths'
import { MenuItem, findNavigationMenuByPath, getNavigationMenu } from './navigationMenu'

import './DashboardStyles.less'

const { Sider } = AntLayout

type AdminDashboardWrapperProps = {
  children?: ReactNode
  dependantTasks: AdminTask[]
  permissions: Permissions[]
  user: User
}

const AdminDashboardWrapper: React.FC<AdminDashboardWrapperProps> = ({
  children,
  dependantTasks,
  permissions,
  user,
}) => {
  const location = useLocation()
  const [activeKey, setActiveKey] = useState<string[] | undefined>()
  const { t: translate } = useTranslation()
  const product = useCurrentProduct()
  const showSmsMessages = useFeatureFlag(Feature.SmsTracking)
  const navigationMenu = getNavigationMenu(product.productId, dependantTasks, showSmsMessages).sort(
    (first, second) => first.priority - second.priority,
  )
  const history = useHistory()

  const [pageTitle] = useSharedState<{
    title: string
    showBackButton: boolean
  }>('PageTitle')

  const [initialLoad, setInitialLoad] = useState<boolean>(false)
  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false)

  useEffect(() => {
    const found = findNavigationMenuByPath(navigationMenu, location.pathname)
    Array.isArray(found)
      ? setActiveKey(found.map((x) => x.key))
      : setActiveKey(found ? [found.key] : [navigationMenu[0].key])
  }, [location])

  const companyPersonalSection = location.pathname.includes(ROUTE_PATHS.ADMIN_DASHBOARD_PATH)
    ? CompanyPersonalSection.Company
    : CompanyPersonalSection.Personal

  const personalSectionEnabled = permissions.some(
    (p) => p === Permissions.AccessEligibleEmployeeDashboard || p === Permissions.AccessNonEligibleEmployeeDashboard,
  )

  const getItem = (item: MenuItem) => {
    return (
      <>
        {item.disabled ? (
          <Tooltip title={translate(item.disabledDescription)} position="right">
            <Icon type={item.icon ?? ''} fontAwesome className="mr-8" />
            {translate(item.name)}
          </Tooltip>
        ) : (
          <>
            <Icon type={item.icon ?? ''} fontAwesome className="mr-8" />
            {translate(item.name)}
            {item.hasBadge && (
              <div className="float-right">
                <Tag className="mr-0" color="#11d175">
                  NEW
                </Tag>
              </div>
            )}
            {item.newTab ? (
              <>
                <a href={item.path ?? ''} target={'_blank'} rel="noreferrer" />
                <Icon
                  className="fas fa-external-link align-center float-right mt-8 pt-4"
                  style={{ color: '#858C96' }}
                />
              </>
            ) : (
              <Link to={item.path ?? ''} />
            )}
          </>
        )}
      </>
    )
  }

  const getChildMenuItems = (children: MenuItem[]): ReactNode[] =>
    children.map((child: MenuItem) =>
      child.children.length > 0 ? (
        <Menu.SubMenu
          className="m-0 submenu-fill-sider"
          key={child.key}
          title={translate(child.name)}
          icon={<Icon fontAwesome className="far fa-envelope" />}
        >
          {child.children.map((item) => (
            <Menu.Item key={item.key} disabled={item.disabled}>
              {getItem(item)}
            </Menu.Item>
          ))}
        </Menu.SubMenu>
      ) : (
        <Menu.Item className="m-0" key={child.key} disabled={child.disabled}>
          {getItem(child)}
        </Menu.Item>
      ),
    )

  const handleDrawerPlacement = useCallback(() => {
    let desktopContainer = document.getElementById('desktop-nav-container')
    let mobileContainer = document.getElementById('mobile-nav-container')
    let navContainer = document.getElementById('nav-container')

    if (window.innerWidth > 992) {
      let children: any = desktopContainer.querySelector('.ant-layout-sider-children')
      children.append(navContainer)
    } else {
      mobileContainer.append(navContainer)
    }
  }, [])

  useEffect(() => {
    setInitialLoad(true)
    handleDrawerPlacement()
    window.addEventListener('resize', handleDrawerPlacement)

    return () => {
      window.removeEventListener('resize', handleDrawerPlacement)
    }
  }, [])

  let linkValue = ''
  switch (product.productId) {
    case ProductIds.Perks:
      linkValue = 'https://workperks.zendesk.com/hc/en-us'
      break
    case ProductIds.IchraId:
      linkValue = 'https://peoplekeephelpichra2.zendesk.com/hc/en-us'
      break
    case ProductIds.QsehraId:
      linkValue = 'https://peoplekeephelp.zendesk.com/hc/en-us'
      break
    default:
      linkValue = 'https://grouphra.zendesk.com/hc/en-us'
      break
  }

  const isUserAdmin =
    user.roles.includes(UserRoles.CompanyAdmin) || user.roles.includes(UserRoles.CompanySecondaryAdmin)

  return (
    <AntLayout>
      <AdminDashboardHeader
        user={user}
        userInfo={{
          firstName: user.firstName,
          lastName: user.lastName,
          isAdmin: isUserAdmin,
        }}
        translate={translate}
        companyDashboardLink={ROUTE_PATHS.DASHBOARD_PATH}
        personalDashboardLink={getPersonalDashboardLink(user.permissions)}
        companyPersonalSection={companyPersonalSection}
        personalSectionEnabled={personalSectionEnabled}
      />
      <AntLayout className="dashboard_body">
        {initialLoad && (
          <DrawerButton>
            <Button
              onClick={() => {
                setIsDrawerVisible(true)
              }}
              className="header-drawer-toggle"
              type="text"
            >
              <Icon type="far fa-bars" fontAwesome />
            </Button>
          </DrawerButton>
        )}
        <Sider width={240} id="desktop-nav-container" className="nav-desktop-container">
          <Menu selectedKeys={activeKey} mode="inline" id="nav-container" className="dashboard-nav">
            {navigationMenu.map((item: any) =>
              item.children.length > 0 ? (
                <Menu.ItemGroup className={'mb-0'} key={item.key} title={translate(item.name)}>
                  {getChildMenuItems(item.children)}
                </Menu.ItemGroup>
              ) : (
                <Menu.Item className={'m-0'} key={item.key} disabled={item.disabled}>
                  {getItem(item)}
                </Menu.Item>
              ),
            )}
            <Menu.Item key="help-link" className={'mt-24 drawer-help-center-link'}>
              <div className="pr-24">
                <Icon type="fa fa-question-circle" fontAwesome className="mr-8" />
                <span>{translate('user-header.help-center')}</span>
                <a href={linkValue} target={'_blank'} rel="noreferrer" />
              </div>
            </Menu.Item>
          </Menu>
        </Sider>

        <Drawer
          width={240}
          open={isDrawerVisible}
          onClose={() => {
            setIsDrawerVisible(false)
          }}
          placement="left"
          rootClassName="nav-mobile-drawer"
          forceRender
        >
          <div id="mobile-nav-container">
            {isUserAdmin ? (
              <div className="flex js-center mt-24 pk--drawer-role-toggler">
                <CompanyPersonalComponent
                  companyDashboardLink={ROUTE_PATHS.DASHBOARD_PATH}
                  personalDashboardLink={getPersonalDashboardLink(user.permissions)}
                  companyPersonalSection={companyPersonalSection}
                  personalSectionEnabled={personalSectionEnabled}
                />
              </div>
            ) : null}
          </div>
        </Drawer>
        <div className="dashboard-content">
          {pageTitle && (
            <div className="flex flex__align_center color-gray-9 py-20 font-20 bg-white pl-24 pr-24 border-color-gray-5-bottom">
              {pageTitle.showBackButton && (
                <Button
                  type="text"
                  size="small"
                  className="mr-8"
                  onClick={() => {
                    history.goBack()
                  }}
                >
                  <Icon className="fa fa-arrow-left" />
                </Button>
              )}
              {pageTitle.title}
            </div>
          )}
          <AntLayout className="dashboard-layout-content">{children}</AntLayout>
        </div>
      </AntLayout>
      <Footer />
    </AntLayout>
  )
}

export default AdminDashboardWrapper

const getPersonalDashboardLink = (permissions) => {
  return permissions.includes(Permissions.AccessEligibleEmployeeDashboard)
    ? ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD
    : ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_DASHBOARD
}

const DrawerButton = ({ children }: { children: JSX.Element }) => {
  return createPortal(children, document?.getElementById('header-navigation-button-container'))
}
